import { DelayMode } from './rist'
import type { Product } from './types'

export const DEFAULT_TRANSFORMATION_DELAY_MS = 1000
export const DEFAULT_BUFFER_DURATION = 6000
export const DEFAULT_DELAY_MODE = parseInt(process.env.DELAY_MODE || '2') as DelayMode.basedOnOriginTime
export const USE_ORIGIN_TIMING = true
export const SRT_STATS_INTERVAL = 10
export const defaultRegionName = process.env.EDGE_DEFAULT_REGION_NAME || 'default'
export const EDGE_OPERATOR_API_TOKEN_NAME = 'edge-operator-api-token'
export const EDGE_SUPER_API_TOKEN_SECRET_NAME = 'edge-operator-api-token'
export const TS_ANALYSIS_INTERVAL_SECONDS = 10

export const DEFAULT_PCAP_DURATION_SECONDS = 10
// Long captures risk filling the appliance disk, which can cause production outages on the video node.
export const MAX_PCAP_DURATION_SECONDS = 3600

export const HEADER_EDGE_DATA_CONTAINER_ID = 'edge-data-container-id'

// EDGE-4072: Sticky ingress during UI proxy:
// ensure that all communicating parties are routed
// through the same edge-api instance by setting this cookie.
export const COOKIE_APPLIANCE_PROXY = 'applianceproxy'

export const PRODUCT_FAMILY_NAME = 'Nimbra Edge'
export const PRODUCTS = {
    nimbraEdge: {
        id: '705be4c2-0e82-4356-9ce2-6484c116796f',
        name: 'Kubernetes-based Nimbra Edge',
    },
    connectIt: {
        id: 'd0b70d6d-8db6-4524-8d4f-7ee716af241a',
        name: 'Docker-based Nimbra Edge',
    },
} as const

export const FRAMEFORMER_QUALITY_TIERS = {
    premium: {
        name: 'Premium',
        serviceType: 'frameformer_premium' as const,
    },
    performance: {
        name: 'Performance',
        serviceType: 'frameformer_performance' as const,
    },
    base: {
        name: 'Base',
        serviceType: 'frameformer_base' as const,
    },
} as const

/** @OAS_NAME FrameformerQualityTier */
export type FrameformerQualityTier = keyof typeof FRAMEFORMER_QUALITY_TIERS
export type FrameformerServiceType = (typeof FRAMEFORMER_QUALITY_TIERS)[FrameformerQualityTier]['serviceType']

export const DEFAULT_FRAMEFORMER_QUALITY_TIER = 'performance' as const satisfies FrameformerQualityTier

export const RTUS = {
    output: {
        id: 'd0c55a7c-fa2b-4ad9-9072-3e52898f39ff',
        serviceType: 'output' as const,
        name: 'Output',
    },
    transcode: {
        id: '1209970e-683d-416e-884d-b573b0ac73f6',
        serviceType: 'transcode' as const,
        name: 'Transcode',
    },
    frameformerPremium: {
        id: 'af9ce29c-5177-426c-96e1-2a948d26b1f8',
        serviceType: FRAMEFORMER_QUALITY_TIERS.premium.serviceType,
        name: 'FrameFormer Premium',
    },
    frameformerPerformance: {
        id: 'ad5c2ef2-61f8-4a67-9b01-dca6a513f8ff',
        serviceType: FRAMEFORMER_QUALITY_TIERS.performance.serviceType,
        name: 'FrameFormer Performance',
    },
    frameformerBase: {
        id: '70c1e47b-bc7f-45fa-95ef-8580dde8ec22',
        serviceType: FRAMEFORMER_QUALITY_TIERS.base.serviceType,
        name: 'FrameFormer Base',
    },
    frameformerDemo: {
        id: '53c5296c-6b8c-4b3d-bce2-0f04042328f0',
        serviceType: 'frameformer_demo' as const,
        name: 'FrameFormer Demo',
    },
    frameformer: {
        id: '6a201cc0-9998-4933-8e5e-c0c7d7d0f2d5',
        serviceType: 'frameformer' as const,
        name: 'FrameFormer',
    },
    adInsertion: {
        id: '2a152fa6-9d77-4a76-9bcb-732f9aeeca9e',
        serviceType: 'ad_insertion' as const,
        name: 'Ad Insertion',
    },
}

export const SERVICE_TYPES = Object.freeze(Object.values(RTUS).map((rtu) => rtu.serviceType))

/** @OAS_NAME ServiceType */
export type ServiceType = (typeof SERVICE_TYPES)[number]

export const FRAMEFORMER_QUALITY_TIER_OPTIONS = Object.freeze(
    Object.entries(FRAMEFORMER_QUALITY_TIERS).map(([value, tier]) => ({
        name: tier.name,
        value: value as FrameformerQualityTier,
    }))
)

/**
 * Service types covered by each FrameFormer RTU type: an RTU also covers all lower tiers, so a
 * customer who changes tier mid-month does not consume an extra RTU or fall back to minute
 * pricing for the time spent on a lower tier. The legacy un-tiered `frameformer` type is
 * equivalent to the default (performance) tier, in both directions: legacy RTUs keep covering
 * usage after an installation upgrades to tier-aware billing, and tier RTUs cover usage reported
 * by appliances still running pre-tier software.
 */
export const FRAMEFORMER_RTU_COVERAGE: Partial<Record<keyof typeof RTUS, readonly ServiceType[]>> = {
    frameformerPremium: ['frameformer_premium', 'frameformer_performance', 'frameformer', 'frameformer_base'],
    frameformerPerformance: ['frameformer_performance', 'frameformer', 'frameformer_base'],
    frameformer: ['frameformer', 'frameformer_performance', 'frameformer_base'],
    frameformerBase: ['frameformer_base'],
}

/**
 * Service types that are reported for usage visibility but are always free: they never produce
 * billable minutes and never consume RTUs. FrameFormer demo mode renders a watermarked
 * evaluation output, so its usage must not cost anything.
 */
export const FREE_SERVICE_TYPES: readonly ServiceType[] = ['frameformer_demo']

export function isFreeServiceType(serviceType: string): boolean {
    return (FREE_SERVICE_TYPES as readonly string[]).includes(serviceType)
}

/** FrameFormer service types from highest to lowest tier, in RTU allocation order */
export const FRAMEFORMER_SERVICE_TYPE_TIER_ORDER: readonly ServiceType[] = [
    'frameformer_premium',
    'frameformer_performance',
    'frameformer',
    'frameformer_base',
    'frameformer_demo',
]

export function isFrameformerQualityTier(value: unknown): value is FrameformerQualityTier {
    // hasOwnProperty.call (not `in`, which would also accept Object.prototype keys like 'toString',
    // and not Object.hasOwn, which needs lib es2022 that appliance-ui's tsconfig does not target)
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(FRAMEFORMER_QUALITY_TIERS, value)
}

export function normalizeFrameformerQualityTier(value: unknown): FrameformerQualityTier {
    return isFrameformerQualityTier(value) ? value : DEFAULT_FRAMEFORMER_QUALITY_TIER
}

export function frameformerServiceTypeForTier(value: unknown): FrameformerServiceType {
    const tier = normalizeFrameformerQualityTier(value)
    return FRAMEFORMER_QUALITY_TIERS[tier].serviceType
}

export function formatServiceTypeName(serviceType: string): string {
    return Object.values(RTUS).find((rtu) => rtu.serviceType === serviceType)?.name ?? serviceType
}

export function findProductById(productId: Product['id']): Product | undefined {
    return Object.values(PRODUCTS).find((p) => p.id === productId)
}

export const TOPIC_AD_AUDIT_LOG = 'ad_audit_log'
export const TOPIC_ALARM_NOTIFICATIONS = 'alarm_notifications'
export const TOPIC_ALARM_SNAPSHOTS = 'alarm_snapshots'

export const EDGE_OS_ID = 'edgeos'
